import { MOODS } from "@/lib/constants";
import { TRAIL_LIST, TRAILS } from "@/lib/trails";

export const DEFAULT_SECTIONS = [
  { key: "mood", label: "Mood filters (What's the mood?)" },
  { key: "featured", label: "Featured & Sponsored" },
  { key: "featured_video", label: "Featured Video" },
  { key: "near_me", label: "Places near you" },
  { key: "today", label: "Happening Today" },
  { key: "locals_love", label: "Locals Love" },
  { key: "videos", label: "Watch (Video reel)" },
  { key: "sports", label: "Get in the game (Sports)" },
  { key: "fitness", label: "Move your body (Fitness)" },
  { key: "submit_cta", label: "Submit-a-listing banner" },
  { key: "wine_beer", label: "Wine & Beer Trail promo" },
  { key: "weekend", label: "This Weekend" },
];

export const DEFAULT_NAV = [
  { key: "home", label: "Home", path: "/" },
  { key: "explore", label: "Explore", path: "/explore" },
  { key: "events", label: "Events", path: "/events" },
  { key: "trails", label: "Trails", path: "/trails" },
  { key: "videos", label: "Watch", path: "/videos" },
  { key: "itinerary", label: "Plan", path: "/itinerary" },
  { key: "saved", label: "Saved", path: "/saved" },
];

export const DEFAULT_THEME = { primary_hex: "#C0492E", accent_hex: "#E0A419", font: "classic" };

export const FONT_PAIRINGS = [
  {
    key: "classic",
    label: "Classic Southern (Playfair + Outfit)",
    heading: "Playfair Display",
    body: "Outfit",
    url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700;800&family=Outfit:wght@300;400;500;600;700&display=swap",
  },
  {
    key: "modern",
    label: "Modern Editorial (Fraunces + Work Sans)",
    heading: "Fraunces",
    body: "Work Sans",
    url: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Work+Sans:wght@300;400;500;600;700&display=swap",
  },
  {
    key: "elegant",
    label: "Elegant Serif (Cormorant + Nunito Sans)",
    heading: "Cormorant Garamond",
    body: "Nunito Sans",
    url: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Nunito+Sans:wght@300;400;600;700&display=swap",
  },
  {
    key: "friendly",
    label: "Friendly Round (Poppins)",
    heading: "Poppins",
    body: "Poppins",
    url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap",
  },
];

export function resolveMoods(s) {
  return s?.moods?.length ? s.moods : MOODS;
}

export function resolveSections(s) {
  const saved = s?.home_sections?.length ? s.home_sections : DEFAULT_SECTIONS.map((x) => ({ ...x, visible: true }));
  const keys = new Set(saved.map((x) => x.key));
  const result = [...saved];
  // Insert any brand-new default sections at their natural default position
  // (rather than always at the end) so existing saved layouts still look right.
  DEFAULT_SECTIONS.forEach((d, di) => {
    if (keys.has(d.key)) return;
    let insertAt = 0;
    for (let j = di - 1; j >= 0; j--) {
      const idx = result.findIndex((x) => x.key === DEFAULT_SECTIONS[j].key);
      if (idx !== -1) { insertAt = idx + 1; break; }
    }
    result.splice(insertAt, 0, { ...d, visible: true });
    keys.add(d.key);
  });
  return result;
}

export function resolveNavConfig(s) {
  const saved = s?.nav_items?.length ? s.nav_items : DEFAULT_NAV.map((x) => ({ ...x, visible: true }));
  const keys = new Set(saved.map((x) => x.key));
  const missing = DEFAULT_NAV.filter((d) => !keys.has(d.key)).map((x) => ({ ...x, visible: true }));
  return [...saved, ...missing];
}

// Trail overrides: presentation fields + visible/order, merged onto code defaults.
export function resolveTrailsList(s) {
  const overrides = Object.fromEntries((s?.trails || []).map((t) => [t.slug, t]));
  return TRAIL_LIST
    .map((t, i) => {
      const o = overrides[t.slug] || {};
      return { ...t, ...o, _order: o.order != null ? o.order : i, _visible: o.visible !== false };
    })
    .filter((t) => t._visible)
    .sort((a, b) => a._order - b._order);
}

export function resolveTrail(s, slug) {
  const base = TRAILS[slug];
  if (!base) return null;
  const o = (s?.trails || []).find((t) => t.slug === slug) || {};
  if (o.visible === false) return null;
  return { ...base, ...o };
}

function hexToHslString(hex) {
  if (!hex) return null;
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s; const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTheme(theme) {
  const root = document.documentElement;
  const p = hexToHslString(theme?.primary_hex);
  const a = hexToHslString(theme?.accent_hex);
  if (p) { root.style.setProperty("--primary", p); root.style.setProperty("--ring", p); }
  if (a) root.style.setProperty("--accent", a);
}

export function applyFonts(fontKey) {
  const pair = FONT_PAIRINGS.find((f) => f.key === fontKey) || FONT_PAIRINGS[0];
  let link = document.getElementById("csrago-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "csrago-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = pair.url;
  const root = document.documentElement;
  root.style.setProperty("--font-heading", `'${pair.heading}', Georgia, serif`);
  root.style.setProperty("--font-body", `'${pair.body}', system-ui, sans-serif`);
}

export function applySettings(s) {
  applyTheme(s?.theme);
  applyFonts(s?.theme?.font);
}
